import { useCallback, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

// grecaptcha.execute can hang indefinitely when its iframe fails to load; fall back to v2 instead.
export const V3_TOKEN_TIMEOUT_MS = 5000;

type CaptchaForm = {
  recaptcha_response_v2?: string;
  recaptcha_response_v3?: string;
};

type Options = {
  clearSubmissionErrors: () => void;
  hasV2Token: () => boolean;
  resetV2Token: () => void;
  setGenericError: () => void;
  setRequiredV2Error: () => void;
};

export const useCaptchaFallback = ({
  clearSubmissionErrors,
  hasV2Token,
  resetV2Token,
  setGenericError,
  setRequiredV2Error,
}: Options) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [useV2, setUseV2] = useState(false);
  const [shouldFocusV2, setShouldFocusV2] = useState(false);
  const [isPreparingSubmission, setIsPreparingSubmission] = useState(false);
  const submittedWithV2 = useRef(false);

  const handleV2Focus = useCallback(() => setShouldFocusV2(false), []);

  const escalateToV2 = useCallback(() => setUseV2(true), []);

  const handleCaptchaError = (field: PropertyKey): boolean => {
    if (field === 'recaptcha_response_v3') {
      setUseV2(true);
      setShouldFocusV2(true);
      return true;
    }
    if (field === 'recaptcha_response_v2') {
      resetV2Token();
      setGenericError();
      setShouldFocusV2(true);
      return true;
    }
    return false;
  };

  // v3 tokens expire two minutes after they are minted and are single-use, so every submit mints its own.
  const mintV3Token = async (mountedToken: string | undefined): Promise<string | undefined> => {
    if (!executeRecaptcha) {
      return mountedToken;
    }
    setIsPreparingSubmission(true);
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      const expiry = new Promise<undefined>((resolve) => {
        expiryTimer = setTimeout(() => resolve(undefined), V3_TOKEN_TIMEOUT_MS);
      });
      return (await Promise.race([executeRecaptcha(), expiry])) || undefined;
    } catch {
      return undefined;
    } finally {
      clearTimeout(expiryTimer);
      setIsPreparingSubmission(false);
    }
  };

  const prepareCaptchaSubmission = async <Form extends CaptchaForm>(
    data: Form,
  ): Promise<(Form & { recaptcha_response_v2: string | undefined }) | null> => {
    clearSubmissionErrors();
    if (useV2 && !data.recaptcha_response_v2) {
      setRequiredV2Error();
      setShouldFocusV2(true);
      return null;
    }

    const recaptchaResponseV2 = data.recaptcha_response_v2 || undefined;
    const recaptchaResponseV3 = recaptchaResponseV2
      ? data.recaptcha_response_v3
      : await mintV3Token(data.recaptcha_response_v3);

    if (!recaptchaResponseV2 && !recaptchaResponseV3) {
      setUseV2(true);
      setGenericError();
      setShouldFocusV2(true);
      return null;
    }

    submittedWithV2.current = !!recaptchaResponseV2;
    if (recaptchaResponseV2) {
      resetV2Token();
    }
    return {
      ...data,
      recaptcha_response_v3: recaptchaResponseV3,
      recaptcha_response_v2: recaptchaResponseV2,
    };
  };

  const handleSubmissionError = () => {
    if (submittedWithV2.current && !hasV2Token()) {
      setShouldFocusV2(true);
    }
    submittedWithV2.current = false;
  };

  return {
    escalateToV2,
    handleCaptchaError,
    handleSubmissionError,
    handleV2Focus,
    isPreparingSubmission,
    prepareCaptchaSubmission,
    shouldFocusV2,
    useV2,
  };
};
